import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { TripType } from "@/types/port";
import { useState } from "react";
import { PortSearchForm } from "./PortSearchForm";
import type { PaxCount } from "./PaxSelect";
import { usePurchaseTicketSearchForm } from "./hook/usePurchaseTicketSearchForm";
import { usePorts } from "@/hooks/usePorts";
import { FullScreenLoader } from "@/components/common/FullScreenLoader";
import { Loader2 } from "lucide-react";

export type PortCardInitialValues = {
  tripType: TripType;
  originId: string;
  destinationId: string;
  departureDate: Date;
  returnDate: Date;
  pax: PaxCount;
};

type PortCardProps = {
  initialValues?: PortCardInitialValues;
  submitLabel?: string;
  onSubmitted?: () => void;
};

export default function PortCard({
  initialValues,
  submitLabel = "Search",
  onSubmitted,
}: PortCardProps) {
  const [tripType, setTripType] = useState<TripType>(
    initialValues?.tripType ?? "return",
  );

  const {
    originList,
    destinationList,
    ticketTypeList,
    originId,
    destinationId,
    setOriginId,
    setDestinationId,
    initLoading,
  } = usePorts({
    initialOriginId: initialValues?.originId,
    initialDestinationId: initialValues?.destinationId,
  });

  const origin =
    originList.find((item) => item.mdTerminalId === originId) ?? null;

  const destination =
    destinationList.find((item) => item.mdTerminalId === destinationId) ?? null;

  const [departureDate, setDepartureDate] = useState(
    initialValues?.departureDate ?? new Date(),
  );
  const [returnDate, setReturnDate] = useState(
    initialValues?.tripType === "one-way"
      ? initialValues.departureDate
      : (initialValues?.returnDate ?? new Date()),
  );

  const [pax, setPax] = useState<PaxCount>({
    adultMYS: initialValues?.pax.adultMYS ?? 1,
    childMYS: initialValues?.pax.childMYS ?? 0,
    infantMYS: initialValues?.pax.infantMYS ?? 0,
    adultForeign: initialValues?.pax.adultForeign ?? 0,
    childForeign: initialValues?.pax.childForeign ?? 0,
    infantForeign: initialValues?.pax.infantForeign ?? 0,
    local: initialValues?.pax.local ?? 0,
    foc: initialValues?.pax.foc ?? 0,
    worker: initialValues?.pax.worker ?? 0,
});

  const totalPax = Object.values(pax).reduce((sum, value) => sum + value, 0);

  const { handleSubmit: handleSearchSubmit, searchLoading } =
    usePurchaseTicketSearchForm({
      tripType,
      selectedOrigin: origin
        ? {
            id: origin.mdTerminalId,
            name: origin.mdTerminalName,
          }
        : undefined,
      selectedDestination: destination
        ? {
            id: destination.mdTerminalId,
            name: destination.mdTerminalName,
          }
        : undefined,
      departDate: departureDate,
      returnDate: tripType === "return" ? returnDate : undefined,
      counts: pax,
      passengerTypes: ticketTypeList,
      totalPax,
    });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const success = await handleSearchSubmit();

    if (success) onSubmitted?.();
  };

  return (
    <>
      {initLoading && <FullScreenLoader />}

      <form
        onSubmit={handleSubmit}
        className={cn(
          submitLabel === "Update" ? "mb-7" : "mt-7",
          "rounded-2xl bg-card px-4 py-6 shadow-xl",
        )}
      >
        <div className="grid grid-cols-2 gap-0 rounded-2xl bg-primary-soft p-0 md:w-2xs">
          {(["one-way", "return"] as const).map((type) => (
            <Button
              key={type}
              type="button"
              variant={tripType === type ? "default" : "ghost"}
              className={cn(
                "h-10",
                tripType === type
                  ? "w-full text-sm font-bold uppercase rounded-2xl hover:bg-primary"
                  : "w-full text-sm font-bold uppercase text-foreground hover:bg-transparent",
              )}
              aria-pressed={tripType === type}
              onClick={() => setTripType(type)}
            >
              {type === "one-way" ? "One Way" : "Return"}
            </Button>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-4 lg:flex-row">
          <PortSearchForm
            tripType={tripType}
            origin={origin}
            destination={destination}
            departureDate={departureDate}
            returnDate={returnDate}
            pax={pax}
            originList={originList}
            destinationList={destinationList}
            ticketTypeList={ticketTypeList}
            onOriginChange={(value) => {
              setOriginId(value.mdTerminalId);
            }}
            onDestinationChange={(value) => {
              setDestinationId(value.mdTerminalId);
            }}
            onDepartureDateChange={(date) => {
              setDepartureDate(date);
              setReturnDate((currentReturnDate) =>
                currentReturnDate < date ? date : currentReturnDate,
              );
            }}
            onReturnDateChange={setReturnDate}
            onPaxChange={setPax}
          />
          <div className="flex-1">
            <Button
              type="submit"
              variant="secondary"
              disabled={searchLoading}
              className="mt-4 w-full cursor-pointer"
            >
              {searchLoading ? (
                <>
                  <Loader2 className="animate-spin" /> Searching...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
